package components.project.configuration.layout

import com.meistercharts.model.Insets
import components.form.commentSection
import components.project.blueprint.EditRoof
import components.project.blueprint.roofSizeForm
import components.project.configuration.isConfigurationEditable
import components.project.configuration.layout.moduleLayout.EditUnusableArea
import components.project.configuration.layout.moduleLayout.moduleLayoutForm
import components.project.configuration.layout.moduleLayout.moduleLayoutInsetsForm
import components.project.configuration.layout.moduleLayout.planning.moduleAreaPlanningWithButtons
import components.project.freezableFormButtons
import components.project.isProjectEditable
import components.project.validMetalRoofTileColors
import components.project.validModuleTypes
import components.project.validRoofTileTypes
import components.project.validRoofTypes
import components.project.validationTable
import it.neckar.commons.kotlin.js.getNotNull
import it.neckar.commons.kotlin.js.safeGet
import it.neckar.lizergy.model.configuration.moduleLayout.PvModule
import it.neckar.lizergy.model.configuration.moduleLayout.ResolvedModuleLayout
import it.neckar.lizergy.model.configuration.moduleLayout.planning.ModuleAreasInformation
import it.neckar.lizergy.model.configuration.moduleLayout.planning.PvRoofPlanningModelInformation
import it.neckar.lizergy.model.configuration.moduleLayout.planning.UnusableAreasInformation
import it.neckar.lizergy.model.configuration.quote.QuoteConfiguration
import it.neckar.lizergy.model.project.ResolvedProject
import it.neckar.lizergy.model.project.process.state.current
import it.neckar.lizergy.model.project.process.state.toNewProcessState
import it.neckar.open.collections.fastForEachIndexed
import it.neckar.open.kotlin.lang.nullIfBlank
import it.neckar.open.unit.si.cm
import it.neckar.open.unit.si.mm
import it.neckar.react.common.*
import it.neckar.react.common.form.*
import it.neckar.react.common.form.EditableStatus.*
import it.neckar.react.common.router.*
import plannerI18nConfiguration
import react.*
import react.dom.*
import router.useConfigurationIdFromUrl
import router.useDocumentTitle
import router.useLoadResolvedProjectFromUrl
import router.useModuleLayoutIdFromUrl
import services.UiActions
import store.hooks.useLoadProcessStates
import store.hooks.useLoadProcessStatesForComponent
import store.hooks.useRequireCompanyForLoggedInUser
import store.hooks.useSelectAvailableProducts
import kotlin.math.roundToInt

/**
 * Edits a moduleLayout
 */
val EditConfigurationModuleLayoutFromUrl: FC<Props> = fc("EditConfigurationModuleLayoutFromUrl") {
  val resolvedProject = useLoadResolvedProjectFromUrl()
  val configurationId = useConfigurationIdFromUrl()
  val moduleLayoutId = useModuleLayoutIdFromUrl()
  val companyName = useRequireCompanyForLoggedInUser().simpleName
  val allProcessStatesForComponents = useLoadProcessStates()

  useDocumentTitle(companyName, "Modulflächen", allProcessStatesForComponents?.let { resolvedProject?.getDisplayName(it) }, resolvedProject?.get(configurationId)?.configurationName, resolvedProject?.get(configurationId)?.get(moduleLayoutId)?.layoutName)

  busyIfNull(resolvedProject) { loadedProject ->
    val quoteConfiguration = loadedProject[configurationId]
    val processStatesForConfiguration = useLoadProcessStatesForComponent(quoteConfiguration.uuid)
    val currentProcessStateForConfiguration = processStatesForConfiguration?.current()?.processState ?: quoteConfiguration.processState?.currentValue?.toNewProcessState()
    val editableStatus = loadedProject.isProjectEditable().and(currentProcessStateForConfiguration.isConfigurationEditable())
    val resolvedModuleLayout = quoteConfiguration[moduleLayoutId]

    EditConfigurationModuleLayout {
      attrs {
        this.project = loadedProject
        this.quoteConfiguration = quoteConfiguration
        this.moduleLayout = resolvedModuleLayout
        this.editableStatus = editableStatus
      }
    }
  }
}

val EditConfigurationModuleLayout: FC<EditConfigurationModuleLayoutProps> = fc("EditConfigurationModuleLayout") { props ->
  val navigate = useNavigateUrl()
  val availableProducts = useSelectAvailableProducts()

  val project = props::project.safeGet()
  val quoteConfiguration = props::quoteConfiguration.safeGet()
  val initialModuleLayout = props::moduleLayout.safeGet()
  val editableStatus = props::editableStatus.safeGet()

  val initialRoof = initialModuleLayout.roof

  @cm val roofWidth = useState((initialRoof.size?.width?.div(10.0))?.roundToInt())
  @cm val roofHeight = useState((initialRoof.size?.height?.div(10.0))?.roundToInt())

  val roofInclination = useState(initialRoof.inclination?.angle)
  val roofOrientation = useState(initialRoof.orientation?.orientation)

  val description = useState(initialModuleLayout.description.orEmpty())
  val moduleType = useState(initialModuleLayout.moduleType)

  val suggestedRoofInsetsLeft = useState((initialModuleLayout.suggestedRoofInsets.left / 10.0).roundToInt())
  val suggestedRoofInsetsRight = useState((initialModuleLayout.suggestedRoofInsets.right / 10.0).roundToInt())
  val suggestedRoofInsetsTop = useState((initialModuleLayout.suggestedRoofInsets.top / 10.0).roundToInt())
  val suggestedRoofInsetsBottom = useState((initialModuleLayout.suggestedRoofInsets.bottom / 10.0).roundToInt())

  val moduleAreasInformationToSave = useState(initialModuleLayout.moduleAreasInformation)
  val unusableAreasInformationToSave = useState(initialModuleLayout.unusableAreasInformation)

  val resolvedRoofToSave = useState(initialRoof)


  val suggestedRoofInsets = useMemo(
    suggestedRoofInsetsLeft.value,
    suggestedRoofInsetsRight.value,
    suggestedRoofInsetsTop.value,
    suggestedRoofInsetsBottom.value,
  ) {
    Insets.of(
      top = suggestedRoofInsetsTop.value * 10,
      right = suggestedRoofInsetsRight.value * 10,
      bottom = suggestedRoofInsetsBottom.value * 10,
      left = suggestedRoofInsetsLeft.value * 10,
    )
  }

  val roofSize = resolvedRoofToSave.value.size
  val currentPlanningModel = useMemo(
    moduleType.value.size,
    roofSize,
    suggestedRoofInsets,
    moduleAreasInformationToSave.value,
    unusableAreasInformationToSave.value,
  ) {
    if (roofSize != null) {
      PvRoofPlanningModelInformation(
        moduleSize = moduleType.value.size,
        roofSize = roofSize,
        suggestedRoofInsets = suggestedRoofInsets,
        moduleAreasInformation = moduleAreasInformationToSave.value,
        unusableAreasInformation = unusableAreasInformationToSave.value,
      )
    } else null
  }

  val resolvedModuleLayoutToSave = useMemo(
    description.value,
    moduleType.value,
    resolvedRoofToSave.value,
    suggestedRoofInsets,
    moduleAreasInformationToSave.value,
    unusableAreasInformationToSave.value,
  ) {
    initialModuleLayout.copy(
      description = description.value.nullIfBlank(),
      moduleType = moduleType.value,
      roof = resolvedRoofToSave.value,
      suggestedRoofInsets = suggestedRoofInsets,
      moduleAreasInformation = moduleAreasInformationToSave.value,
      unusableAreasInformation = unusableAreasInformationToSave.value,
    )
  }

  val okAction = {
    UiActions.saveModuleLayout(project, quoteConfiguration, resolvedModuleLayoutToSave)
    navigate.back()
  }


  div {
    if (editableStatus == Editable) {
      onEnter { okAction() }
    }

    h2 {
      +"Modul-Belegung"
    }

    roofSizeForm(
      roofWidth = roofWidth,
      roofHeight = roofHeight,
      roofInclination = roofInclination,
      roofOrientation = roofOrientation,
      editableStatus = editableStatus
    )

    div("my-5") {
      EditModuleLayout {
        attrs {
          this.descriptionToSave = description
          this.moduleTypeToSave = moduleType
          this.suggestedRoofInsetsLeft = suggestedRoofInsetsLeft
          this.suggestedRoofInsetsRight = suggestedRoofInsetsRight
          this.suggestedRoofInsetsTop = suggestedRoofInsetsTop
          this.suggestedRoofInsetsBottom = suggestedRoofInsetsBottom
          this.moduleAreasInformationToSave = moduleAreasInformationToSave
          this.unusableAreasInformationToSave = unusableAreasInformationToSave
          this.descriptionPlaceholder = "${resolvedModuleLayoutToSave.roof.roofName} ${resolvedModuleLayoutToSave.peakPower.formatKiloWattPeak()}"
          this.currentPlanningModel = currentPlanningModel
          this.availableModuleTypes = availableProducts.validModuleTypes(initialModuleLayout.moduleType)
          this.editableStatus = editableStatus
        }
      }
    }

    EditRoof {
      attrs {
        this.resolvedRoofToSave = resolvedRoofToSave
        this.roofWidth = roofWidth
        this.roofHeight = roofHeight
        this.roofInclination = roofInclination
        this.roofOrientation = roofOrientation
        this.availableRoofTypes = availableProducts.validRoofTypes(initialRoof.roofType)
        this.availableRoofTiles = initialRoof.roofTile?.let { availableProducts.validRoofTileTypes(it) } ?: availableProducts.availableRoofTiles()
        this.availableMetalRoofTileColors = initialRoof.metalRoofTileConstructionType?.color?.let { availableProducts.validMetalRoofTileColors(it) } ?: availableProducts.availableMetalRoofTileColors()
        this.totalModulesCount = resolvedModuleLayoutToSave.modulesCount
        this.blackModules = moduleType.value.isBlackModule
        this.editableStatus = editableStatus
      }
    }

    div("mb-5") {
      h3("mb-2") {
        +"Bemerkungen Dachfläche"
      }
      project.blueprint.roofAnnotations.firstOrNull { it.id == initialRoof.id }?.let {
        it.annotation?.let { p { +it } }
      }
      commentSection(initialRoof)
    }

    freezableFormButtons(editableStatus) { okAction() }

    div("my-5") {
      validationTable(resolvedModuleLayoutToSave.validationProblems)
    }
  }

}

val EditModuleLayout: FC<EditModuleLayoutProps> = fc("EditModuleLayout") { props ->
  val descriptionToSave = props::descriptionToSave.getNotNull()
  val moduleTypeToSave = props::moduleTypeToSave.getNotNull()
  val suggestedRoofInsetsLeft = props::suggestedRoofInsetsLeft.getNotNull()
  val suggestedRoofInsetsRight = props::suggestedRoofInsetsRight.getNotNull()
  val suggestedRoofInsetsTop = props::suggestedRoofInsetsTop.getNotNull()
  val suggestedRoofInsetsBottom = props::suggestedRoofInsetsBottom.getNotNull()
  val moduleAreasInformationToSave = props::moduleAreasInformationToSave.getNotNull()
  val unusableAreasInformationToSave = props::unusableAreasInformationToSave.getNotNull()

  val currentPlanningModel = props::currentPlanningModel.safeGet()
  val availableModuleTypes = props::availableModuleTypes.safeGet()
  val editableStatus = props::editableStatus.safeGet()

  val descriptionPlaceholder = props::descriptionPlaceholder.safeGet()

  val modulesCount = moduleAreasInformationToSave.value.modulesCount()


  div("my-3") {
    moduleLayoutInsetsForm(
      suggestedRoofInsetsLeft = suggestedRoofInsetsLeft,
      suggestedRoofInsetsRight = suggestedRoofInsetsRight,
      suggestedRoofInsetsTop = suggestedRoofInsetsTop,
      suggestedRoofInsetsBottom = suggestedRoofInsetsBottom,
      editableStatus = editableStatus,
    )
  }

  div("my-3") {
    moduleLayoutForm(
      label = descriptionToSave,
      placeholder = descriptionPlaceholder,
      moduleType = moduleTypeToSave,
      availableModuleTypes = availableModuleTypes,
      editableStatus = editableStatus,
    )

    currentPlanningModel?.let {
      div("row mb-3") {
        div("col-sm-12") {
          moduleAreaPlanningWithButtons(
            model = it,
            editableStatus = editableStatus,
          ) { updated ->
            //Update grid, unusable areas and modules when changed
            moduleAreasInformationToSave.setter(updated.moduleAreasInformation)
            unusableAreasInformationToSave.setter(updated.unusableAreasInformation)
          }
        }
      }

      p("form-text") {
        +"$modulesCount Module | Leistung: ${(moduleTypeToSave.value.powerRating * modulesCount).formatKiloWattPeak(plannerI18nConfiguration)}"
      }

      unusableAreasInformationToSave.value.unusableAreas.fastForEachIndexed { index, unusableArea ->
        EditUnusableArea {
          attrs {
            this.unusableAreasInformationToSave = unusableAreasInformationToSave
            this.unusableArea = unusableArea
            this.index = index
            this.roofSize = currentPlanningModel.roofSize
            this.editableStatus = editableStatus
          }
        }
      }
    }
  }
}


external interface EditConfigurationModuleLayoutProps : Props {
  var project: ResolvedProject
  var quoteConfiguration: QuoteConfiguration
  var moduleLayout: ResolvedModuleLayout
  var editableStatus: EditableStatus
}

external interface EditModuleLayoutProps : Props {
  var descriptionToSave: StateInstance<String>
  var moduleTypeToSave: StateInstance<PvModule>
  var suggestedRoofInsetsLeft: StateInstance<@mm Int>
  var suggestedRoofInsetsRight: StateInstance<@mm Int>
  var suggestedRoofInsetsTop: StateInstance<@mm Int>
  var suggestedRoofInsetsBottom: StateInstance<@mm Int>
  var moduleAreasInformationToSave: StateInstance<ModuleAreasInformation>
  var unusableAreasInformationToSave: StateInstance<UnusableAreasInformation>
  var descriptionPlaceholder: String?
  var currentPlanningModel: PvRoofPlanningModelInformation?
  var availableModuleTypes: List<PvModule>
  var editableStatus: EditableStatus
}
